import { useState } from "react";
import {
  Button,
  EditButton,
  TopToolbar,
  usePermissions,
  useShowContext,
  type RaRecord,
} from "react-admin";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import GroupsIcon from "@mui/icons-material/Groups";
import PaymentsIcon from "@mui/icons-material/Payments";
import ConstructionIcon from "@mui/icons-material/Construction";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import { useMediaQuery } from "@mui/material";
import { hasPermission } from "@/utils/permission-utils";
import { navigateToBaseRoute } from "@/utils/route-utility";

export const ProjectShowToolbar = () => {
  const { record } = useShowContext<RaRecord>();
  const { permissions } = usePermissions();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("sm"), { noSsr: true });
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const closeMenu = () => setAnchorEl(null);

  const toolbarSx = {
    flexWrap: "wrap" as const,
    gap: 1,
    display: "flex",
    minHeight: 48,
    py: isSmall ? 0.5 : 1,
    my: isSmall ? 0.5 : 1,
    visibility: "visible" as const,
    overflow: "visible" as const,
  };

  const canReceipt = hasPermission(permissions, "projectTakhmeenReceipts.create");
  const canTakhmeen = hasPermission(permissions, "projectTakhmeens.create");
  const canInayat = hasPermission(permissions, "projectInayats.create");
  const canWorkPayment =
    hasPermission(permissions, "projectPayments.create") ||
    hasPermission(permissions, "projects.edit") ||
    hasPermission(permissions, "projects.*");
  const canKarzan = hasPermission(permissions, "projectKarzans.create");
  const canRepayment = hasPermission(permissions, "projectKarzanRepayments.create");
  const hasAnyCreate =
    canReceipt || canTakhmeen || canInayat || canWorkPayment || canKarzan || canRepayment;

  if (!record?.id) {
    return (
      <TopToolbar sx={toolbarSx}>
        <EditButton />
      </TopToolbar>
    );
  }

  const projectId = String(record.id);

  return (
    <TopToolbar sx={toolbarSx}>
      {hasPermission(permissions, "projects.edit") && <EditButton />}
      {hasAnyCreate && (
        <>
          <Button
            id="project-add-menu-button"
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            endIcon={<KeyboardArrowDownIcon />}
            aria-controls={menuOpen ? "project-add-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            label={isSmall ? "Add" : "Add to project"}
          />
          <Menu
            id="project-add-menu"
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={closeMenu}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            MenuListProps={{ "aria-labelledby": "project-add-menu-button", dense: true }}
          >
            {canReceipt && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  navigateToBaseRoute("projects", projectId, ["projectTakhmeenReceipts", "create"]);
                }}
              >
                <ListItemIcon>
                  <ReceiptLongIcon fontSize="small" color="success" />
                </ListItemIcon>
                <ListItemText primary="Add receipt" />
              </MenuItem>
            )}
            {canTakhmeen && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  navigateToBaseRoute("projects", projectId, ["projectTakhmeens", "create"]);
                }}
              >
                <ListItemIcon>
                  <GroupsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Add takhmeen pledge" />
              </MenuItem>
            )}
            {canInayat && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  navigateToBaseRoute("projects", projectId, ["projectInayats", "create"]);
                }}
              >
                <ListItemIcon>
                  <VolunteerActivismIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Add Inayat" />
              </MenuItem>
            )}
            {canWorkPayment && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  navigateToBaseRoute("projects", projectId, ["projectPayments", "create"]);
                }}
              >
                <ListItemIcon>
                  <ConstructionIcon fontSize="small" color="warning" />
                </ListItemIcon>
                <ListItemText primary="Add work payment" />
              </MenuItem>
            )}
            {canKarzan && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  navigateToBaseRoute("projects", projectId, ["projectKarzans", "create"]);
                }}
              >
                <ListItemIcon>
                  <AccountBalanceIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Add Karzan" />
              </MenuItem>
            )}
            {canRepayment && (
              <MenuItem
                onClick={() => {
                  closeMenu();
                  navigateToBaseRoute("projects", projectId, ["projectKarzanRepayments", "create"]);
                }}
              >
                <ListItemIcon>
                  <PaymentsIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Add Karzan repayment" />
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </TopToolbar>
  );
};
